import React, { Component } from "react";
import moment from "moment";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { compose } from "redux";
import MainHeader from "components/templateParts/MainHeader";
import dataProvider, { GET_ONE, UPDATE } from "providers/dataProvider";
import { withAuthCenter } from "AuthCenter";
import { withNotificationCenter } from "NotificationCenter";
import withForm from "withForm";
import styles from "./EditTrf.module.scss";
import clsx from "clsx";
import { Box, Typography, Button, List, ListItem, Paper } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BaseForm from "components/ui_components/BaseForm/BaseForm";
import ProgressBar from "components/ui_components/ProgressBar/ProgressBar";
import WidgetPatientContainer from 'components/pages/requisitions/RequisitionsCreateNew/WidgetPatientContainer';
import {
    convertDateCollectedFromToServerUTC,
    convertDateReceivedFromToServerUTC,
    getClientTzFromExtraSelectedOptionData,
} from './reqCreateHelpers';
import {
    inputsByHash,
    inputsById,
    inputsGroupsById,
    inputsGroupsByHash
} from "config/resources/requisitionsResource/create_new";
import printBarcode from "printer/layout/printBarcode.js";
import printCryo from "printer/layout/printCryo.js";
import Dymo from "printer/printer";

const dymo = new Dymo({
    hostname: "127.0.0.1",
});

class EditTrfContainer extends Component {
    _isMounted = false;

    constructor(props) {
        super(props);
        this.state = {
            data: null,
            loading: true,
            submitting: false,
            successData: null,
            selectedPatient: null,
            isNewPatient: false,
            selectedPatientIsEdited: false,
            forceSaveRequired: false,
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadData();
    }

    componentDidUpdate(prevProps) {
        const currentId = this.props.reqID || (this.props.match && this.props.match.params && this.props.match.params.reqId);
        const prevId = prevProps.reqID || (prevProps.match && prevProps.match.params && prevProps.match.params.reqId);

        if (currentId !== prevId) {
            this.loadData();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    loadData = () => {
        const reqId = this.props.reqID || (this.props.match && this.props.match.params && this.props.match.params.reqId);

        // Reset local state and form errors when navigating to a new TRF in a batch
        this.setState({ loading: true, successData: null, forceSaveRequired: false });
        if (this.props.resetErrors) {
            this.props.resetErrors();
        }

        dataProvider(GET_ONE, "requisitions", { id: reqId })
            .then(({ data }) => {
                if (this._isMounted) {
                    // Reset form fields to defaults before loading new data to avoid field data persistence (e.g. email not clearing)
                    this.props.clearInputs(true).then(() => {
                        this.setState({ data, loading: false });

                        if (data) {
                            const updateInputs = [];

                            // Map basic fields
                            Object.keys(inputsByHash).forEach(id => {
                                if (data.hasOwnProperty(id)) {
                                    const inputConfig = inputsByHash[id];
                                    let val = data[id];

                                    // Special handling for multi-selects (e.g. failIds): extract IDs if they are objects
                                    if (inputConfig.subType === 'multi' && Array.isArray(val)) {
                                        val = val.map(item => (typeof item === 'object' && item !== null ? (item.id || item.value) : item));
                                    }

                                    updateInputs.push({ id, value: val !== null ? val : (inputConfig.subType === 'multi' ? [] : '') });
                                }
                            });

                            // Pre-populate failIds specifically if only failCodes is present
                            if (data.failCodes && (!data.failIds || data.failIds.length === 0)) {
                                const failIds = data.failCodes.map(fc => fc.id || fc);
                                updateInputs.push({ id: 'failIds', value: failIds });
                            }

                            // Container Type and Specimen Type: pre-fill from server data
                            if (data.containerTypeCode) {
                                updateInputs.push({ id: 'containerTypeCode', value: data.containerTypeCode });
                            }
                            if (data.specimenTypeCode) {
                                updateInputs.push({ id: 'specimenTypeCode', value: data.specimenTypeCode });
                                // Also pre-fill specContainerTypeCode for backward compat (if it's used elsewhere)
                                updateInputs.push({ id: 'specContainerTypeCode', value: data.specimenTypeCode });
                            }
                            // TRF form uses `specimenConditionCode` but backend stores `temperatureTypeCode`.
                            if (data.temperatureTypeCode) {
                                updateInputs.push({ id: 'specimenConditionCode', value: data.temperatureTypeCode });
                            }
                            // TRF form uses `collaborator` but payload contains `colaId`.
                            if (data.colaId) {
                                updateInputs.push({ id: 'collaborator', value: data.colaId });
                            }
                            if (data.specContainerTypeCode) {
                                updateInputs.push({ id: 'specContainerTypeCode', value: data.specContainerTypeCode });
                            }

                            // Billing field renames: map old server keys → new form input keys
                            if (data.isSelfPay !== undefined) {
                                updateInputs.push({ id: 'isSelfPayProvided', value: data.isSelfPay !== null ? data.isSelfPay : 0 });
                            }

                            // Always ensure patientEmail is updated (or cleared to empty string if null)
                            updateInputs.push({ id: 'patientEmail', value: data.billingEmail || '' });

                            // Handle date mapping for single DOB field
                            if (data.patientDob) {
                                const dob = moment.utc(data.patientDob);
                                updateInputs.push({
                                    id: 'patientDob',
                                    value: dob.isValid() ? dob.format("MM / DD / YYYY") : ''
                                });
                            }

                            if (data.dateCollected) {
                                const dc = moment.utc(data.dateCollected);
                                const isValidDate = dc.isValid();
                                const dcYear = dc.year();

                                if (!isValidDate || dcYear === 1900 || dcYear === 2000) {
                                    // Invalid date or sentinel/placeholder date — default collected time to 0
                                    updateInputs.push({ id: 'dateCollected', value: dc.isValid() ? dc.format("MM / DD / YYYY") : '' });
                                    updateInputs.push({ id: 'timeCollected', value: '00 : 00' });
                                    updateInputs.push({ id: 'timeCollectedAmPm', value: 'am' });
                                } else {
                                    updateInputs.push({
                                        id: 'dateCollected',
                                        value: dc.isValid() ? dc.format("MM / DD / YYYY") : ''
                                    });

                                    let hours = dc.hours();
                                    const ampm = hours >= 12 ? 'pm' : 'am';
                                    hours = hours % 12;
                                    hours = hours ? hours : 12;

                                    updateInputs.push({
                                        id: 'timeCollected',
                                        value: `${String(hours)} : ${String(dc.minutes()).padStart(2, '0')}`
                                    });
                                    updateInputs.push({ id: 'timeCollectedAmPm', value: ampm });
                                }
                            }

                            if (data.dateReceived) {
                                const dr = moment.utc(data.dateReceived);
                                if (dr.isValid()) {
                                    updateInputs.push({
                                        id: 'dateReceived',
                                        value: dr.format("MM / DD / YYYY")
                                    });
                                    let receivedHours = dr.hours();
                                    const receivedAmPm = receivedHours >= 12 ? 'pm' : 'am';
                                    receivedHours = receivedHours % 12;
                                    receivedHours = receivedHours ? receivedHours : 12;
                                    updateInputs.push({
                                        id: 'timeReceived',
                                        value: `${String(receivedHours)} : ${String(dr.minutes()).padStart(2, '0')}`
                                    });
                                    updateInputs.push({ id: 'dateReceivedAmPm', value: receivedAmPm });
                                }
                            } else {
                                // If database dateReceived is missing, ensure it defaults to today as per config
                                updateInputs.push({
                                    id: 'dateReceived',
                                    value: moment(new Date()).format("MM / DD / YYYY")
                                });
                                updateInputs.push({
                                    id: 'timeReceived',
                                    value: moment(new Date()).format("hh : mm")
                                });
                                updateInputs.push({
                                    id: 'dateReceivedAmPm',
                                    value: moment(new Date()).format("a")
                                });
                            }

                            this.props.handleBulkInputs(updateInputs);
                        }
                    });
                }
            })
            .catch(error => {
                if (this._isMounted) {
                    this.setState({ loading: false });
                    this.props.notificationCenter.show(error.message, "error");
                }
            });
    };

    handlePrintCryo = async () => {
        const { data } = this.state;
        try {
            await printCryo(dymo, data);
        } catch (err) {
            console.error(err);
            this.props.notificationCenter.show("Printing error: Please make sure DYMO Connect is running.", "error");
        }
    };

    handlePrintBarCode = async () => {
        const { data } = this.state;
        try {
            await printBarcode(dymo, data);
        } catch (err) {
            console.error(err);
            this.props.notificationCenter.show("Printing error: Please make sure DYMO Connect is running.", "error");
        }
    };

    handleSave = (e) => {
        if (e) e.preventDefault();

        const { isFormValid, getSubmitData, getExtraSelectedOptionData, notificationCenter, resetErrors, authCenter } = this.props;
        const { data: reqData } = this.state;

        const isPlaceHolder = reqData.isPlaceHolderReq === 1 || (reqData.isPlaceHolderReq !== 0 && (reqData.isPlaceHolder || reqData.patientLastName === "[PLACE_HOLDER]" || reqData.patientFirstName === "[PLACE_HOLDER]" || reqData.clientName === "[PLACE_HOLDER]"));
        if (isPlaceHolder) {
            const currentUserId = authCenter?.access?.userId;
            if (String(reqData.trfQueueUserId) !== String(currentUserId)) {
                const assignedMessage = reqData.trfQueueUserName ? `assigned to ${reqData.trfQueueUserName}` : `not assigned to anyone`;
                const confirmMsg = `Warning: This placeholder TRF is ${assignedMessage}.\n\nAre you sure you truly want to update it?`;
                if (!window.confirm(confirmMsg)) {
                    return;
                }
            }
        }

        if (!isFormValid()) {
            notificationCenter.show("Please fix the errors in the form", "warning");
            return;
        }

        resetErrors();
        const allFieldsData = getSubmitData(false);

        // Validation
        let dob = allFieldsData.patientDob;

        if (!dob || dob.trim() === '') {
            dob = '01/01/1900';
        }

        try {
            if (dob !== '01/01/1900') {
                const mDob = moment(dob.replace(/\s/g, ""), "MM/DD/YYYY", true);
                if (!mDob.isValid()) throw new Error("Invalid Date of Birth format. Please use MM/DD/YYYY");
                if (mDob.isAfter(moment())) throw new Error("Date of Birth cannot be in the future");
            }
        } catch (e) {
            notificationCenter.show(e.message, 'error');
            return;
        }

        let extraSelectedOptionData = getExtraSelectedOptionData();
        let clientTz = getClientTzFromExtraSelectedOptionData(extraSelectedOptionData) || reqData.clientTz || reqData.collectedTz;

        // Fallback to browser timezone if no timezone is provided by client or requisition record
        if (!clientTz || clientTz === "TZNA") {
            clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        }

        let dateCollectedInUTC = null;
        if (!allFieldsData.dateCollected) {
            allFieldsData.dateCollected = '01 / 01 / 1900';
        }

        try {
            dateCollectedInUTC = convertDateCollectedFromToServerUTC(allFieldsData, clientTz);
        } catch (e) {
            notificationCenter.show(e.message, 'error');
            return;
        }

        let dateReceivedInUTC = null;
        if (!allFieldsData.dateReceived) {
            allFieldsData.dateReceived = '01 / 01 / 1900';
        }

        try {
            dateReceivedInUTC = convertDateReceivedFromToServerUTC(allFieldsData, clientTz);
        } catch (e) {
            notificationCenter.show(e.message, "error");
            return;
        }

        if (allFieldsData.isDiabetic === null || allFieldsData.isDiabetic === undefined) {
            allFieldsData.isDiabetic = 0;
        }
        if (allFieldsData.isFamilyHistory === null || allFieldsData.isFamilyHistory === undefined) {
            allFieldsData.isFamilyHistory = 0;
        }

        // Build the raw submit data (all fields, including excludeSubmit ones)
        const rawData = getSubmitData();
        const {
            collaborator,           // will be sent as colaId
            specimenConditionCode,  // will be sent as temperatureTypeCode
            ...restData
        } = rawData;

        // Apply health and boolean defaults to restData to prevent NoneType/float errors on Backend
        if (restData.isDiabetic === null || restData.isDiabetic === undefined || restData.isDiabetic === "") {
            restData.isDiabetic = 0;
        }
        if (restData.isFamilyHistory === null || restData.isFamilyHistory === undefined || restData.isFamilyHistory === "") {
            restData.isFamilyHistory = 0;
        }
        if (restData.isProviderSignature === null || restData.isProviderSignature === undefined || restData.isProviderSignature === "") {
            restData.isProviderSignature = 0;
        }
        if (restData.isInsCardProvided === null || restData.isInsCardProvided === undefined || restData.isInsCardProvided === "") {
            restData.isInsCardProvided = 0;
        }
        if (restData.isSelfPayProvided === null || restData.isSelfPayProvided === undefined || restData.isSelfPayProvided === "") {
            restData.isSelfPayProvided = 0;
        }
        if (restData.isIcdProvided === null || restData.isIcdProvided === undefined || restData.isIcdProvided === "") {
            restData.isIcdProvided = 0;
        }

        const patientFirst = (allFieldsData.patientFirstName || '').trim();
        const patientLast = (allFieldsData.patientLastName || '').trim();

        // Detect if IDs have changed from original reqData to avoid false positive placeholder checks
        const currentClientId = allFieldsData.clientId;
        const isOriginalClient = reqData && String(currentClientId) === String(reqData.clientId);
        const clientName = (extraSelectedOptionData.clientId && extraSelectedOptionData.clientId.clientName) || (isOriginalClient ? reqData.clientName : '');

        const currentPhysId = allFieldsData.orderingClientPhysId;
        const isOriginalPhys = reqData && String(currentPhysId) === String(reqData.orderingClientPhysId);
        const physName = (extraSelectedOptionData.orderingClientPhysId && extraSelectedOptionData.orderingClientPhysId.label) || (isOriginalPhys ? `${reqData.physFirstName} ${reqData.physLastName}` : '');

        if (!this.state.forceSaveRequired) {
            let warnings = [];

            // 1. Client name <> '[PLACE_HOLDER]'
            const isClientPlaceholder = clientName === '[PLACE_HOLDER]';
            if (isClientPlaceholder || !allFieldsData.clientId) {
                warnings.push({ path: 'clientId', message: 'Warning: Please select a valid Client' });
            }

            // Check if timezone fallback was used (determined by checking if clientTz matches browser TZ and was originally missing)
            const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            const originalClientTz = getClientTzFromExtraSelectedOptionData(extraSelectedOptionData) || reqData.clientTz || reqData.collectedTz;
            if (clientTz === browserTz && (!originalClientTz || originalClientTz === 'TZNA') && !isClientPlaceholder) {
                warnings.push({ path: 'clientId', message: 'Warning: Client timezone not set. Using browser timezone.' });
            }

            // 2. Physician first/last name <> '[PLACE_HOLDER]'
            if (!allFieldsData.orderingClientPhysId || physName === '[PLACE_HOLDER] [PLACE_HOLDER]' || physName?.includes('[PLACE_HOLDER]')) {
                warnings.push({ path: 'orderingClientPhysId', message: 'Warning: Please select a valid Physician' });
            }

            // 3. Patient DOB <> '01/01/1900' or '12/31/1899'
            const dobIsPlaceholder = dob === '01/01/1900' || dob === '12/31/1899' || !dob;
            if (dobIsPlaceholder) {
                warnings.push({ path: 'patientDob', message: 'Warning' });
            }

            // 4. Patient first/last name <> '[PLACE_HOLDER]'
            if (!patientFirst || patientFirst === '[PLACE_HOLDER]') warnings.push({ path: 'patientFirstName', message: 'Warning' });
            if (!patientLast || patientLast === '[PLACE_HOLDER]') warnings.push({ path: 'patientLastName', message: 'Warning' });

            // 5. Date/time collected empty or '01/01/1900'
            const dcStr = allFieldsData.dateCollected || '';
            const dcParts = dcStr.split('/').map(p => p.trim());
            const dcMonth = dcParts[0] || '01';
            const dcDate = dcParts[1] || '01';
            const dcYear = dcParts[2] || '1900';
            const dcIsPlaceholder = (dcMonth === '01' && dcDate === '01' && dcYear === '1900') || !dcStr;

            if (dcIsPlaceholder) {
                warnings.push({ path: 'dateCollected', message: 'Warning: Date Collected is required' });
            }

            // 6. Test Ordered empty
            if (!allFieldsData.testOrderedCodes || allFieldsData.testOrderedCodes.length === 0) {
                warnings.push({ path: 'testOrderedCodes', message: 'Warning' });
            }

            // 7. Specimen container type <> Serum in SST. If not Serum in SST, default tube count to 0
            if (allFieldsData.specContainerTypeCode !== 'SERUM-SST') {
                warnings.push({ path: 'specContainerTypeCode', message: 'Warning' });
            }

            // 8. Patient email empty
            if (!allFieldsData.patientEmail || allFieldsData.patientEmail.trim() === '') {
                warnings.push({ path: 'patientEmail', message: 'Warning' });
            }

            // 9. Guarantor address empty
            if (!allFieldsData.guarantorAddr1 || allFieldsData.guarantorAddr1.trim() === '') {
                warnings.push({ path: 'guarantorAddr1', message: 'Warning' });
            }

            // 10. Diabetic status — warn if not explicitly set (server returned null)
            const diabeticVal = allFieldsData.isDiabetic;
            if (diabeticVal === null || diabeticVal === undefined || diabeticVal === '') {
                warnings.push({ path: 'isDiabetic', message: 'Warning: Diabetic status not confirmed' });
            }

            // 11. Family History — warn if not explicitly set (server returned null)
            const familyHistVal = allFieldsData.isFamilyHistory;
            if (familyHistVal === null || familyHistVal === undefined || familyHistVal === '') {
                warnings.push({ path: 'isFamilyHistory', message: 'Warning: Family History not confirmed' });
            }

            if (warnings.length > 0) {
                this.props.handleErrors({ inner: warnings });
                this.setState({ forceSaveRequired: true });
                notificationCenter.show("Validation warnings found. Review highlighted fields and click 'Save Anyway' to continue.", "warning");

                // If not Serum in SST, default tube count to 0 in state
                if (allFieldsData.specContainerTypeCode !== 'SERUM-SST') {
                    this.props.handleInputs({ target: { name: 'tubeCount', value: 0 } });
                }

                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        // Additional validation for current year and placeholders as requested in image
        if (!this.state.forceSaveRequired) {
            const currentYearStr = new Date().getFullYear().toString();
            let additionalWarnings = [];

            // Year validation
            const drStr = allFieldsData.dateReceived || '';
            const drParts = drStr.split('/').map(p => p.trim());
            const drYear = drParts[2];
            
            const dcValStr = allFieldsData.dateCollected || '';
            const dcValParts = dcValStr.split('/').map(p => p.trim());
            const dcValYear = dcValParts[2];

            if (dcValYear && dcValYear !== currentYearStr) {
                additionalWarnings.push({ path: 'dateCollected', message: `Warning: Year must be the current year (${currentYearStr})` });
            }
            if (drYear && drYear !== currentYearStr) {
                additionalWarnings.push({ path: 'dateReceived', message: `Warning: Year must be the current year (${currentYearStr})` });
            }

            // Enhanced placeholder validation for Client and Physician
            if (clientName?.includes('[PLACE_HOLDER]') || allFieldsData.clientId === 0 || allFieldsData.clientId === '0') {
                additionalWarnings.push({ path: 'clientId', message: 'Warning: Please select a valid Client' });
            }
            if (physName?.includes('[PLACE_HOLDER]') || allFieldsData.orderingClientPhysId === 0 || allFieldsData.orderingClientPhysId === '0') {
                additionalWarnings.push({ path: 'orderingClientPhysId', message: 'Warning: Please select a valid Physician' });
            }

            if (additionalWarnings.length > 0) {
                this.props.handleErrors({ inner: additionalWarnings });
                this.setState({ forceSaveRequired: true });
                notificationCenter.show("Validation warnings: Review highlighted fields (Year or Placeholders) and click 'Save Anyway' if intentional.", "warning");
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            }
        }

        // "Old Patient ID": always use the requisition's existing patientId record to update it
        // even if the information was imported from a suggested patient.
        const resolvedPatientId = (reqData && reqData.patientId) || null;


        const sendData = {
            ...restData,
            clientId: extraSelectedOptionData.clientId?.id || (typeof extraSelectedOptionData.clientId === 'object' ? null : extraSelectedOptionData.clientId) || reqData.clientId,
            colaId: collaborator,
            temperatureTypeCode: specimenConditionCode,
            testOrderedCodes: allFieldsData.testOrderedCodes || [],
            failIds: allFieldsData.failIds || [],
            patientId: resolvedPatientId,
            patientDob: allFieldsData.patientDob?.replace(/\s/g, ""),
            dateCollected: dateCollectedInUTC,
            dateReceived: dateReceivedInUTC,
            tubeCount: (allFieldsData.tubeCount === 0 || allFieldsData.tubeCount === '0') ? 0 : (allFieldsData.tubeCount || 1),
            reqStatusCode: reqData.reqStatusCode,
            reqId: reqData.id,
        };

        // Convert empty string fields to null before sending to BE
        const emptyToNull = (obj) =>
            Object.fromEntries(
                Object.entries(obj).map(([k, v]) => [
                    k,
                    typeof v === 'string' && v.trim() === '' ? null : v,
                ])
            );
        const finalData = emptyToNull(sendData);

        this.setState({ submitting: true });

        dataProvider(UPDATE, `requisitions/trf/${reqData.id}`, { data: finalData })
            .then((response) => {
                if (this._isMounted) {
                    // Update the local data state so the header reflects the saved changes
                    const updatedData = {
                        ...this.state.data,
                        patientFirstName: finalData.patientFirstName,
                        patientLastName: finalData.patientLastName,
                        patientDob: finalData.patientDob,
                    };

                    this.setState({
                        data: updatedData,
                        submitting: false,
                        successData: {
                            message: response.message || "Requisition updated successfully",
                            issues: response.data?.issues || [],
                            failCodes: response.data?.failCodes || []
                        }
                    });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            })
            .catch(error => {
                if (this._isMounted) {
                    this.setState({ submitting: false });
                    notificationCenter.show(error.message, "error");
                }
            });
    };

    handleNextTrf = () => {
        const reqId = parseInt(
            this.props.reqID || (this.props.match && this.props.match.params && this.props.match.params.reqId)
        );

        // Read batchIds from the URL query param (e.g. ?batchIds=10,11,12)
        const batchIdsParam = this.props.searchParams?.get('batchIds');
        if (batchIdsParam) {
            const batchIds = batchIdsParam.split(',').map(Number).sort((a, b) => a - b);
            const currentIndex = batchIds.indexOf(reqId);
            const nextId = batchIds[currentIndex + 1];

            if (nextId) {
                // Navigate to next TRF in the batch, keeping batchIds in the URL
                this.props.navigate(`/edit-trf/${nextId}?batchIds=${batchIdsParam}`);
            } else {
                // Last TRF in the batch — go back to the list
                alert('You have completed your TRF batch. Please generate new batch.');
                this.props.navigate('/requisitions');
            }
        } else {
            // Fallback: not in batch mode, just go back to the list
            this.props.navigate('/requisitions');
        }
    };

    handleClickSuggestedPatient = (patient) => {
        this.setState({
            selectedPatient: patient,
            selectedPatientIsEdited: false,
        });

        const updateInputs = [];
        // Update all patient fields in the form from the selected patient
        inputsGroupsByHash.createPatient.inputsById.forEach(id => {
            let value = patient[id];

            // Handle missing separate DOB/gender fields in suggestion data
            if (value === undefined || value === null) {
                if (id === 'patientDob') {
                    if (patient.patientDob) {
                        const date = moment.utc(patient.patientDob);
                        if (date.isValid()) {
                            value = date.format("MM/DD/YYYY");
                        }
                    }
                }
                if (id === 'patientGenderCode' && patient.genderCode) {
                    value = patient.genderCode;
                }
            }

            updateInputs.push({ id, value: value || '' });
        });
        this.props.handleBulkInputs(updateInputs);
    };

    handleNewPatientCheck = () => {
        this.setState(prevState => ({
            isNewPatient: !prevState.isNewPatient,
        }));
    };

    handleEditSelectedPatient = () => {
        this.setState({ selectedPatientIsEdited: true });
    };

    // Intercept input changes to auto-formatting and auto-set tubeCount
    handleInputsWrapper = (e) => {
        const name = e?.target?.name;
        const value = e?.target?.value;

        // Auto-formatting for Date fields
        if ((name === 'patientDob' || name === 'dateCollected' || name === 'dateReceived') && value) {
            // Remove non-numeric characters
            let clean = value.replace(/\D/g, '');
            if (clean.length > 8) clean = clean.substring(0, 8);

            // Re-apply formatting MM / DD / YYYY
            let formatted = '';
            if (clean.length > 0) {
                formatted += clean.substring(0, 2);
                if (clean.length > 2) {
                    formatted += ' / ' + clean.substring(2, 4);
                    if (clean.length > 4) {
                        formatted += ' / ' + clean.substring(4, 8);
                    }
                }
            }
            // Update the event value so the underlying handler gets the formatted string
            e.target.value = formatted;
        }

        // Auto-formatting for Time fields
        if ((name === 'timeCollected' || name === 'timeReceived') && value) {
            // Remove non-numeric characters
            let clean = value.replace(/\D/g, '');
            if (clean.length > 4) clean = clean.substring(0, 4);

            // Re-apply formatting HH : MM
            let formatted = '';
            if (clean.length > 0) {
                formatted += clean.substring(0, 2);
                if (clean.length > 2) {
                    formatted += ' : ' + clean.substring(2, 4);
                }
            }
            e.target.value = formatted;
        }

        // Call source handleInputs at the end (or after formatting) so state receives the FINAL value
        this.props.handleInputs(e);

        // Auto-set tubeCount when Microtainer / micro collection type is selected
        if ((name === 'specContainerTypeCode' || name === 'containerTypeCode') && value) {
            const normalizedValue = String(value).toUpperCase();
            const isMicroCollection = normalizedValue.includes('MICRO') || normalizedValue.includes('MICROTUBE') || normalizedValue.includes('MICROTAINER') || normalizedValue.includes('SERUM-MICRO');
            if (isMicroCollection) {
                // Defer slightly so the primary handleInputs state update settles first
                setTimeout(() => {
                    this.props.handleInputs({ target: { name: 'tubeCount', value: 0 } });
                }, 0);
            }
        }
    };

    formatPatientInfo = (data) => {
        if (!data) return '';
        const { id, patientLastName, patientFirstName, patientDob } = data;
        let dobStr = '';
        if (patientDob) {
            const date = new Date(patientDob);
            const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
            const dd = String(date.getUTCDate()).padStart(2, '0');
            const yyyy = date.getUTCFullYear();
            dobStr = ` (${mm}/${dd}/${yyyy})`;
        }
        const name = [patientLastName, patientFirstName].filter(Boolean).join(', ');
        return `#${id}${name ? ` | ${name}` : ''}${dobStr}`;
    };

    render() {
        const { data, loading, submitting, successData } = this.state;
        const { inputsByHash, handleBlur } = this.props;
        const handleInputs = this.handleInputsWrapper;

        if (loading) return <ProgressBar />;
        if (!data) return <Typography sx={{ p: 4, textAlign: 'center' }}>Requisition not found</Typography>;

        const baseFormStyles = {
            rootStyles: 'max-width: none;',
            inputWrapStyles: 'max-width: 180px; width: 100%;',
            groupStyles: 'padding: 0;',
            groupTitleStyles: 'font-weight: 700; font-size: 18px; margin: 10px 0 0;',
        };

        const reqId = parseInt(
            this.props.reqID || (this.props.match && this.props.match.params && this.props.match.params.reqId)
        );
        const batchIdsParam = this.props.searchParams?.get('batchIds');
        let hasNextTrf = false;
        let isBatchMode = false;
        if (batchIdsParam) {
            isBatchMode = true;
            const batchIds = batchIdsParam.split(',').map(Number).sort((a, b) => a - b);
            const currentIndex = batchIds.indexOf(reqId);
            hasNextTrf = currentIndex + 1 < batchIds.length;
        }

        const { isTab } = this.props;


        return (
            <div className={styles.root}>
                {!isTab && <MainHeader title="Edit TRF" />}

                {!isTab && (
                    <div className={styles.introBar}>
                        <div className={styles.introBar__inner}>
                            <div className={styles.headerRow}>
                                <Typography variant="h5" className={styles.patientInfo}>
                                    {this.formatPatientInfo(data)}
                                </Typography>
                                <Typography variant="h5" className={styles.statusLabel}>
                                    {data.reqStatusDesc}
                                </Typography>
                            </div>
                        </div>
                    </div>
                )}

                <div className={clsx("singlePage__container", styles.formWrapper)}>
                    <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

                        <div style={{ flex: 1, minWidth: 0 }}>
                            <form onSubmit={this.handleSave}>
                                {submitting && <ProgressBar />}

                                <div className={styles.widgetsWrapper}>
                                    <div className={styles.container}>
                                        <Paper elevation={1} style={{ padding: '10px 15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {inputsGroupsById.map((groupID) => {
                                                const group = inputsGroupsByHash[groupID];
                                                const isFirst = groupID === inputsGroupsById[0];
                                                const sectionTitle = group.title ? (
                                                    <h3 style={{
                                                        fontSize: '18px',
                                                        margin: isFirst ? '0 0 5px 0' : '10px 0 5px 0',
                                                        color: '#333'
                                                    }}>
                                                        {group.title}
                                                    </h3>
                                                ) : null;

                                                if (groupID === 'createPatient') {
                                                    // Build submitData the same way as RequisitionsCreateContainer:
                                                    // iterate inputsById flat list and read current field values
                                                    const submitData = {};
                                                    (group.inputsById || []).forEach(id => {
                                                        submitData[id] = (this.props.inputsByHash[id] || {}).value;
                                                    });

                                                    return (
                                                        <div key={groupID}>
                                                            {sectionTitle}
                                                            <WidgetPatientContainer
                                                                baseFormStyles={baseFormStyles}
                                                                inputsByHash={inputsByHash}
                                                                inputsById={group.inputsById}
                                                                inputsGroupsByHash={group.inputsGroupsByHash}
                                                                inputsGroupsById={group.inputsGroupsById}
                                                                handleInputs={handleInputs}
                                                                handleBlur={handleBlur}
                                                                submitData={submitData}
                                                                handleSelectSuggestedPatient={this.props.handleBulkInputs}
                                                                handleClickSuggestedPatient={this.handleClickSuggestedPatient}
                                                                handleNewPatientCheck={this.handleNewPatientCheck}
                                                                handleEditSelectedPatient={this.handleEditSelectedPatient}
                                                                isNewPatient={this.state.isNewPatient}
                                                                hideWidgetWrapper={true}
                                                            />
                                                        </div>
                                                    );
                                                }

                                                if (groupID === 'specimenInformation') {
                                                    const renderInlineSubGroup = (subGroupId) => {
                                                        const subGroup = group.inputsGroupsByHash[subGroupId];
                                                        if (!subGroup) return null;
                                                        const subGroupNoTitle = { ...subGroup, title: '' };
                                                        return (
                                                            <div key={subGroupId} style={{ flex: 1 }}>
                                                                {subGroup.title && (
                                                                    <div style={{ fontWeight: 700, fontSize: '13px', color: '#333', marginBottom: '2px' }}>
                                                                        {subGroup.title}
                                                                    </div>
                                                                )}
                                                                <BaseForm
                                                                    {...baseFormStyles}
                                                                    groupStyles="padding: 0;"
                                                                    inputsByHash={inputsByHash}
                                                                    inputsGroupsByHash={{ [subGroupId]: subGroupNoTitle }}
                                                                    inputsGroupsById={[subGroupId]}
                                                                    handleInputs={handleInputs}
                                                                    handleBlur={handleBlur}
                                                                    smartInputOptions={{ clientId: data.clientId, reqID: String(data.id) }}
                                                                />
                                                            </div>
                                                        );
                                                    };

                                                    return (
                                                        <div key={groupID}>
                                                            <BaseForm
                                                                {...baseFormStyles}
                                                                inputsByHash={inputsByHash}
                                                                inputsGroupsByHash={{ group1: group.inputsGroupsByHash.group1 }}
                                                                inputsGroupsById={['group1']}
                                                                handleInputs={handleInputs}
                                                                handleBlur={handleBlur}
                                                                smartInputOptions={{ clientId: data.clientId, reqID: String(data.id) }}
                                                            />
                                                            <div style={{ display: 'flex', gap: '24px', marginTop: '10px', flexWrap: 'wrap' }}>
                                                                {renderInlineSubGroup('collectedGroup')}
                                                                {renderInlineSubGroup('receivedGroup')}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <div key={groupID}>
                                                        {sectionTitle}
                                                        <BaseForm
                                                            {...baseFormStyles}
                                                            inputsByHash={inputsByHash}
                                                            inputsGroupsByHash={group.inputsGroupsByHash}
                                                            inputsGroupsById={group.inputsGroupsById}
                                                            handleInputs={handleInputs}
                                                            handleBlur={handleBlur}
                                                            smartInputOptions={{ clientId: data.clientId, reqID: String(data.id) }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </Paper>
                                    </div>
                                </div>

                                {!successData && (
                                    <div className={styles.controls}>
                                        <Button
                                            className={styles.cancelButton}
                                            onClick={() => this.props.navigate('/requisitions')}
                                            style={{ boxShadow: 'none' }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={submitting}
                                            sx={{ backgroundColor: this.state.forceSaveRequired ? '#d32f2f' : '#004C6A', '&:hover': { backgroundColor: this.state.forceSaveRequired ? '#9a0007' : '#003a52' } }}
                                        >
                                            {this.state.forceSaveRequired ? 'Save Anyway' : 'Save'}
                                        </Button>
                                    </div>
                                )}
                            </form>
                        </div>

                        {successData && (
                            <div style={{ width: '370px', flexShrink: 1, position: 'sticky', top: '20px' }}>
                                <Paper className={styles.successSection}>
                                    <div className={styles.successMessage}>
                                        <CheckCircleOutlineIcon />
                                        <Typography variant="h6">
                                            {successData.message}
                                        </Typography>
                                    </div>

                                    <Box sx={{ mt: 1, mb: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            sx={{
                                                backgroundColor: 'white',
                                                color: '#444',
                                                border: '1px solid #004C6A',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                padding: '6px 20px',
                                                fontSize: '15px',
                                                '&:hover': {
                                                    backgroundColor: '#f0f7ff',
                                                    borderColor: '#003a52',
                                                },
                                            }}
                                            onClick={this.handlePrintBarCode}
                                        >
                                            Barcode
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            sx={{
                                                backgroundColor: 'white',
                                                color: '#444',
                                                border: '1px solid #004C6A',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                padding: '6px 20px',
                                                fontSize: '15px',
                                                '&:hover': {
                                                    backgroundColor: '#f0f7ff',
                                                    borderColor: '#003a52',
                                                },
                                            }}
                                            onClick={this.handlePrintCryo}
                                        >
                                            Cryo
                                        </Button>
                                    </Box>

                                    {successData.issues && successData.issues.length > 0 && (
                                        <Box sx={{ mt: 2, pl: 4 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Issues Found:
                                            </Typography>
                                            <List className={styles.issueList}>
                                                {successData.issues.map((issue, index) => (
                                                    <ListItem key={`issue-${index}`} disableGutters disablePadding className={styles.issueItem}>
                                                        <span className={styles.issueCode}>{issue.issueCode}</span>
                                                        <span className={styles.issueDesc}>{issue.issueDesc}</span>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}

                                    {successData.failCodes && successData.failCodes.length > 0 && (
                                        <Box sx={{ mt: 2, pl: 4 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Hard Stop Issues:
                                            </Typography>
                                            <List className={styles.failCodeList}>
                                                {successData.failCodes.map((failCode, index) => (
                                                    <ListItem key={`failcode-${index}`} disableGutters disablePadding className={styles.failCodeItem}>
                                                        <span className={styles.failCode}>{failCode.reasonCode}</span>
                                                        <span className={styles.failDesc}>{failCode.reasonDesc || failCode.reasonComment}</span>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            className={styles.editRequisitionButton}
                                            onClick={() => window.open(`/requisitions/${reqId}/show`, '_blank')}
                                        >
                                            Edit Requisition
                                        </Button>
                                        <Button
                                            variant="contained"
                                            className={styles.nextButton}
                                            onClick={this.handleNextTrf}
                                        >
                                            {isBatchMode ? (hasNextTrf ? 'Next TRF' : 'Finish Batch') : 'Back to List'}
                                        </Button>
                                    </Box>
                                </Paper>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        );
    }
}

const EditTrfWithRouter = (props) => {
    const params = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    return <EditTrfContainer {...props} match={{ params }} navigate={navigate} searchParams={searchParams} />;
};

export default compose(
    withAuthCenter(),
    withNotificationCenter(),
    withForm({
        inputsByHash,
        inputsById,
    }),
)(EditTrfWithRouter);
